import React, {useState, useEffect} from "react";
import axios from "axios";
import {ToastContainer, toast} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {CheckCircle, DollarSign, Percent, Settings} from "lucide-react";

const InvoiceSettingModern = () => {
  const [settings, setSettings] = useState({
    taxRate: "",
    price: "",
    taxRateFreelance: "",
    priceFreelance: "",
    taxRateRecruiter: "",
    priceRecruiter: "",
    taxRateJobSlot: "",
    priceJobSlot: "",
  });
  const [isJobRunning, setIsJobRunning] = useState(false);

  useEffect(() => {
    setIsJobRunning(
      JSON.parse(localStorage.getItem("isJobRunning") || "false")
    );
  }, []);

  useEffect(() => {
    localStorage.setItem("isJobRunning", JSON.stringify(isJobRunning));
  }, [isJobRunning]);

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      const [defaultRes, freelanceRes, recruiterRes, jobslotRes] =
        await Promise.all([
          axios.get("/api2/GetInvoiceSettings"),
          axios.get("/api2/GetInvoiceSettingsFreelance"),
          axios.get("/api2/GetInvoiceSettingsByType?setting_type=recruiter"),
          axios.get("/api2/GetInvoiceSettingsByType?setting_type=jobslot"),
        ]);

      setSettings((prev) => ({
        ...prev,
        price: defaultRes?.data?.[0]?.priceRate ?? "",
        taxRate: defaultRes?.data?.[0]?.taxRate ?? "",
        priceFreelance: freelanceRes?.data?.[0]?.priceRate ?? "",
        taxRateFreelance: freelanceRes?.data?.[0]?.taxRate ?? "",
        priceRecruiter: recruiterRes?.data?.priceRate ?? "",
        taxRateRecruiter: recruiterRes?.data?.taxRate ?? "",
        priceJobSlot: jobslotRes?.data?.priceRate ?? "",
        taxRateJobSlot: jobslotRes?.data?.taxRate ?? "",
      }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };
  const handleChange = (e) => {
    const {name, value} = e.target;
    setSettings((prev) => ({...prev, [name]: value}));
  };

  const saveSettings = async (type) => {
    try {
      if (type === "default") {
        await axios.post("/api2/UpdateInvoiceSettings", {
          taxRate: settings.taxRate,
          price: settings.price,
        });
      } else if (type === "freelance") {
        await axios.post("/api2/UpdateInvoiceSettingsFreelance", {
          taxRate: settings.taxRateFreelance,
          price: settings.priceFreelance,
        });
      } else if (type === "recruiter") {
        await axios.post("/api2/UpdateInvoiceSettingsByType", {
          setting_type: "recruiter",
          taxRate: settings.taxRateRecruiter,
          price: settings.priceRecruiter,
        });
      } else if (type === "jobslot") {
        await axios.post("/api2/UpdateInvoiceSettingsByType", {
          setting_type: "jobslot",
          taxRate: settings.taxRateJobSlot,
          price: settings.priceJobSlot,
        });
      }
      toast.success("Einstellungen gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern.");
      console.error(error);
    }
  };

  const toggleJobStatus = async () => {
    try {
      const endpoint = isJobRunning
        ? "/api2/stop-cron-job"
        : "/api2/start-cron-job";
      await axios.post(endpoint);
      setIsJobRunning(!isJobRunning);
    } catch (error) {
      console.error("Job status toggle failed:", error);
    }
  };

  const renderSettingCard = (
    title,
    namePrefix,
    priceKey,
    taxKey,
    onSave,
    showJobToggle = false
  ) => (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <Settings size={20} style={{marginRight: 8}} /> {title}
      </div>
      <div style={styles.cardBody}>
        <div style={styles.fieldRow}>
          <label style={styles.label}>
            <Percent size={14} /> Steuer
          </label>
          <input
            type="number"
            name={taxKey}
            value={settings[taxKey]}
            onChange={handleChange}
            style={styles.input}
          />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>
            <DollarSign size={14} /> Preis
          </label>
          <input
            type="number"
            name={priceKey}
            value={settings[priceKey]}
            onChange={handleChange}
            style={styles.input}
          />
        </div>
        <button onClick={onSave} style={styles.saveButton}>
          <CheckCircle size={16} /> Speichern
        </button>
        {showJobToggle && (
          <button onClick={toggleJobStatus} style={styles.toggleButton}>
            {isJobRunning ? "Rechnungen pausieren" : "Rechnungen starten"}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="content">
      <div style={styles.wrapper}>
        <ToastContainer />
        <div style={styles.gridContainer}>
          {renderSettingCard(
            "Nutzungsgebühr",
            "default",
            "price",
            "taxRate",
            () => saveSettings("default"),
            true
          )}
          {renderSettingCard(
            "Befristete Jobs",
            "freelance",
            "priceFreelance",
            "taxRateFreelance",
            () => saveSettings("freelance")
          )}
          {renderSettingCard(
            "Recruiter Flexible",
            "recruiter",
            "priceRecruiter",
            "taxRateRecruiter",
            () => saveSettings("recruiter")
          )}
          {renderSettingCard(
            "Job-Slot Preis",
            "jobslot",
            "priceJobSlot",
            "taxRateJobSlot",
            () => saveSettings("jobslot")
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceSettingModern;

const styles = {
  wrapper: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: 24,
    fontFamily: "system-ui",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 20,
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: 20,
    marginBottom: 20,
  },
  cardHeader: {
    fontSize: 18,
    fontWeight: 600,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  fieldRow: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: 4,
    fontSize: 14,
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  input: {
    padding: 8,
    fontSize: 14,
    borderRadius: 6,
    border: "1px solid #d1d5db",
  },
  saveButton: {
    marginTop: 10,
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  toggleButton: {
    marginTop: 10,
    backgroundColor: "#f59e0b",
    color: "white",
    border: "none",
    padding: "10px 16px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
};
